import React, { useState, useEffect, useRef } from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useDispatch } from 'react-redux';
import useWebSocket from 'hooks/useWebSocket';
import NotificationsListView from '../NotificationsListView';
import { notifyPipelineNotification } from '../../../state/app';

const NotificationIcon = () => {
  const { notifications } = useWebSocket();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [localNotifications, setLocalNotifications] = useState([]);
  const [isGlowing, setIsGlowing] = useState(false);

  // Keep track of cleared notification IDs to prevent them from reappearing
  const clearedNotificationIdsRef = useRef(new Set());

  // Keep track of dispatched notification IDs using a ref
  const dispatchedNotificationIdsRef = useRef(new Set());

  // Keep track of notifications that have been processed
  const processedNotificationIdsRef = useRef(new Set());

  // Sync local notifications with WebSocket notifications
  useEffect(() => {
    if (notifications && notifications.length > 0) {
      setLocalNotifications(prevNotifications => {
        const existingIds = new Set(prevNotifications.map(n => n.id));
        // Filter out notifications that have been cleared
        const newNotifications = notifications.filter(n => 
          !existingIds.has(n.id) && !clearedNotificationIdsRef.current.has(n.id)
        );

        // Only add truly new notifications, don't update existing ones
        if (newNotifications.length > 0) {
          const newNotificationsWithState = newNotifications.map(notification => ({
            ...notification,
            read: false // New notifications are always unread
          }));

          // Check for pipeline status update notifications and dispatch to Redux
          // Only dispatch if we haven't dispatched this notification before
          newNotificationsWithState.forEach(notification => {
            if (notification.subject === "Pipeline_Status_Update" && !dispatchedNotificationIdsRef.current.has(notification.id)) {
              dispatch(notifyPipelineNotification(notification));
              dispatchedNotificationIdsRef.current.add(notification.id);
            }
          });

          // Check for new notifications that haven't been processed
          const newUnprocessedNotifications = newNotificationsWithState.filter(
            notification => !processedNotificationIdsRef.current.has(notification.id)
          );

          if (newUnprocessedNotifications.length > 0) {
            // Add all new notification IDs to the processed set
            newUnprocessedNotifications.forEach(notification => {
              processedNotificationIdsRef.current.add(notification.id);
            });

            // Activate the glow effect
            setIsGlowing(true);
          }

          return [...newNotificationsWithState, ...prevNotifications];
        }

        // If no new notifications, return existing state unchanged
        return prevNotifications;
      });
    }
  }, [notifications, dispatch]);

  // Effect to handle the glow timing
  useEffect(() => {
    let glowTimer;
    if (isGlowing) {
      // Set a timer to turn off the glow after 5 seconds
      glowTimer = setTimeout(() => {
        setIsGlowing(false);
      }, 5000);
    }

    // Clean up the timer when component unmounts or isGlowing changes
    return () => {
      if (glowTimer) {
        clearTimeout(glowTimer);
      }
    };
  }, [isGlowing]);


  // Count unread notifications from local state
  const unreadCount = localNotifications.filter(notification => !notification.read).length;


  // Handle click on notification icon
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle close of notifications popup
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle notification state updates from NotificationsListView
  const handleNotificationUpdate = (updatedNotifications) => {
    // If notifications are cleared (empty array), add all current notification IDs to clearedNotificationIdsRef
    if (updatedNotifications.length === 0 && localNotifications.length > 0) {
      localNotifications.forEach(notification => {
        clearedNotificationIdsRef.current.add(notification.id);
      });
    }

    setLocalNotifications(updatedNotifications);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{
            marginRight: 1
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
          >
            <NotificationsIcon 
              sx={{ 
                color: '#555',
                animation: isGlowing ? 'glow 1s ease-in-out infinite alternate, wiggle 0.6s ease-in-out infinite' : 'none',
                '@keyframes glow': {
                  '0%': { filter: 'drop-shadow(0 0 2px rgba(0, 105, 218, 0.7))' },
                  '100%': { filter: 'drop-shadow(0 0 8px rgba(0, 105, 218, 0.9))' }
                },
                '@keyframes wiggle': {
                  '0%': { transform: 'rotate(0deg)' },
                  '25%': { transform: 'rotate(-10deg)' },
                  '50%': { transform: 'rotate(0deg)' },
                  '75%': { transform: 'rotate(10deg)' },
                  '100%': { transform: 'rotate(0deg)' }
                }
              }} 
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <NotificationsListView
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        notifications={localNotifications}
        onNotificationUpdate={handleNotificationUpdate}
      />
    </>
  );
};

export default NotificationIcon;
